import { toLocalISODate, parseLocalDate } from '../utils/dateUtils';
import React, { useState, useEffect } from 'react';
import api from '../services/api';
import { reportLoadError } from '../utils/notify';
import { useLanguage } from '../contexts/LanguageContext';
import { totalsByCurrency } from '../utils/currencyTotals';
import { CalendarDays, Gift, Landmark, Receipt, RefreshCw, TriangleAlert, Wallet } from 'lucide-react';
import StatusBadge from '../components/StatusBadge';
import './Reports.css';

const Reports = () => {
  const { t, locale } = useLanguage();
  const [activeReport, setActiveReport] = useState('attendance');
  const [loading, setLoading] = useState(false);
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [selectedEmployee, setSelectedEmployee] = useState('all');
  
  // Data states
  const [attendanceReport, setAttendanceReport] = useState([]);
  const [payrollSummary, setPayrollSummary] = useState(null);
  const [taxReport, setTaxReport] = useState([]);
  const [benefitsReport, setBenefitsReport] = useState([]);
  const [expensesReport, setExpensesReport] = useState([]);
  const [truncated, setTruncated] = useState(false);
  const [employees, setEmployees] = useState([]);

  useEffect(() => {
    loadEmployees();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    loadReport();
  }, [activeReport, selectedMonth, selectedYear, selectedEmployee]);

  const loadEmployees = async () => {
    try {
      const response = await api.get('/employees');
      setEmployees(response.data.employees || []);
    } catch (error) {
      console.error('Error loading employees:', error);
      reportLoadError(error, t('Error loading employees'));
    }
  };

  const loadReport = async () => {
    setLoading(true);
    setTruncated(false);
    try {
      switch (activeReport) {
        case 'attendance':
          await loadAttendanceReport();
          break;
        case 'payroll':
          await loadPayrollSummary();
          break;
        case 'taxes':
          await loadTaxReport();
          break;
        case 'benefits':
          await loadBenefitsReport();
          break;
        case 'expenses':
          await loadExpensesReport();
          break;
        default:
          break;
      }
    } catch (error) {
      console.error('Error loading report:', error);
      reportLoadError(error, t('Error loading report'), { main: true });
    } finally {
      setLoading(false);
    }
  };

  const loadAttendanceReport = async () => {
    try {
      const startDate = `${selectedYear}-${String(selectedMonth).padStart(2, '0')}-01`;
      const endDate = toLocalISODate(new Date(selectedYear, selectedMonth, 0));
      
      const params = {
        start_date: startDate,
        end_date: endDate
      };
      
      if (selectedEmployee !== 'all') {
        params.employee_id = selectedEmployee;
      }

      const response = await api.get('/attendance', { params });
      setAttendanceReport(response.data.attendance || []);
    } catch (error) {
      console.error('Error loading attendance:', error);
      reportLoadError(error, t('Error loading attendance report'), { main: true });
      setAttendanceReport([]);
    }
  };

  const loadPayrollSummary = async () => {
    try {
      const startDate = `${selectedYear}-${String(selectedMonth).padStart(2, '0')}-01`;
      const endDate = toLocalISODate(new Date(selectedYear, selectedMonth, 0));
      
      const response = await api.get('/payroll', {
        params: {
          period_end_from: startDate,
          period_end_to: endDate,
          // consolidating and paying move an approved payroll on; it still counts
          statuses: 'approved,consolidated,paid'
        }
      });

      const payrolls = response.data.payroll || [];
      
      // Calculate summary
      const summary = {
        total_employees: payrolls.length,
        // Per currency: SRD and USD used to be added together.
        total_gross: totalsByCurrency(payrolls, p => p.gross_pay, p => p.currency),
        total_taxes: totalsByCurrency(payrolls, p => p.tax_amount || p.total_taxes, p => p.currency),
        total_deductions: totalsByCurrency(payrolls, p => p.total_deductions, p => p.currency),
        total_withheld: totalsByCurrency(payrolls, p => (parseFloat(p.tax_amount || p.total_taxes) || 0) + (parseFloat(p.total_deductions) || 0), p => p.currency),
        total_net: totalsByCurrency(payrolls, p => p.net_pay, p => p.currency),
        payrolls: payrolls
      };

      setPayrollSummary(summary);
      setTruncated(Boolean(response.data.truncated));
    } catch (error) {
      console.error('Error loading payroll summary:', error);
      reportLoadError(error, t('Error loading payroll summary'), { main: true });
      setPayrollSummary(null);
    }
  };

  const loadTaxReport = async () => {
    try {
      const startDate = `${selectedYear}-${String(selectedMonth).padStart(2, '0')}-01`;
      const endDate = toLocalISODate(new Date(selectedYear, selectedMonth, 0));
      
      const response = await api.get('/payroll', {
        params: {
          period_end_from: startDate,
          period_end_to: endDate,
          // consolidating and paying move an approved payroll on; it still counts
          statuses: 'approved,consolidated,paid'
        }
      });

      const payrolls = response.data.payroll || [];
      
      // Extract tax details from each payroll
      const taxData = [];
      payrolls.forEach(payroll => {
        if (payroll.tax_details) {
          try {
            const taxes = typeof payroll.tax_details === 'string' 
              ? JSON.parse(payroll.tax_details) 
              : payroll.tax_details;
            
            Object.values(taxes).forEach(tax => {
              taxData.push({
                employee_id: payroll.employee_id,
                employee_name: `${payroll.first_name} ${payroll.last_name}`,
                payment_date: payroll.payment_date,
                currency: payroll.currency || 'SRD',
                tax_code: tax.code,
                tax_name: tax.name,
                tax_amount: parseFloat(tax.amount || 0),
                tax_rate: parseFloat(tax.rate || 0),
                tax_type: tax.type
              });
            });
          } catch (e) {
            console.error('Error parsing tax details:', e);
          }
        }
      });

      setTaxReport(taxData);
      setTruncated(Boolean(response.data.truncated));
    } catch (error) {
      console.error('Error loading tax report:', error);
      setTaxReport([]);
    }
  };

  const loadBenefitsReport = async () => {
    try {
      const response = await api.get('/benefits', {
        params: {
          status: 'active'
        }
      });

      setBenefitsReport(response.data.benefits || []);
    } catch (error) {
      console.error('Error loading benefits:', error);
      setBenefitsReport([]);
    }
  };

  const loadExpensesReport = async () => {
    try {
      const startDate = `${selectedYear}-${String(selectedMonth).padStart(2, '0')}-01`;
      const endDate = toLocalISODate(new Date(selectedYear, selectedMonth, 0));
      
      const response = await api.get('/finance/expenses', {
        params: {
          start_date: startDate,
          end_date: endDate
        }
      });

      setExpensesReport(response.data.expenses || []);
    } catch (error) {
      console.error('Error loading expenses:', error);
      setExpensesReport([]);
    }
  };
 
  const months = [
    t('January'), t('February'), t('March'), t('April'), t('May'), t('June'),
    t('July'), t('August'), t('September'), t('October'), t('November'), t('December')
  ];

  const years = Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - i);

  const formatCurrency = (amount, currency = 'SRD') => {
    return `${currency} ${parseFloat(amount || 0).toLocaleString(locale, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  // A total per currency (totalsByCurrency), one line each.
  const formatTotals = (totals) => (totals.length === 0 ? [{ currency: 'SRD', amount: 0 }] : totals)
    .map(({ currency, amount }) => (
      <span key={currency} className="rep-currency-line">{formatCurrency(amount, currency)}</span>
    ));

  const formatDate = (date) => {
    if (!date) return '-';
    return parseLocalDate(date)?.toLocaleDateString(locale, { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric' 
    });
  };
  const formatTime = (timeString) => {
    if (!timeString) return '-';
    const parts = timeString.split(':');
    if (parts.length >= 2) {
      const hour = parseInt(parts[0]);
      const minute = parts[1];
      const period = hour >= 12 ? t('PM') : t('AM');
      const hour12 = hour % 12 || 12;
      return `${hour12}:${minute} ${period}`;
    }
    return timeString;
  };

  return (
    <div className="reports-page">
      <div className="page-head">
        <div>
          <h1 className="page-title">{t('Reports & Analytics')}</h1>
          <p className="page-sub">{t('Comprehensive business reports and insights')}</p>
        </div>
      </div>

      {/* Report Type Tabs */}
      <div className="tabbar">
        <button 
          className={`tabbar__tab${activeReport === 'attendance' ? ' is-active' : ''}`}
          onClick={() => setActiveReport('attendance')}
        >
          <CalendarDays aria-hidden="true" /> {t('Attendance')}
        </button>
        <button 
          className={`tabbar__tab${activeReport === 'payroll' ? ' is-active' : ''}`}
          onClick={() => setActiveReport('payroll')}
        >
          <Wallet aria-hidden="true" /> {t('Payroll Summary')}
        </button>
        <button 
          className={`tabbar__tab${activeReport === 'taxes' ? ' is-active' : ''}`}
          onClick={() => setActiveReport('taxes')}
        >
          <Landmark aria-hidden="true" /> {t('Tax Report')}
        </button>
        <button 
          className={`tabbar__tab${activeReport === 'benefits' ? ' is-active' : ''}`}
          onClick={() => setActiveReport('benefits')}
        >
          <Gift aria-hidden="true" /> {t('Benefits')}
        </button>
        <button 
          className={`tabbar__tab${activeReport === 'expenses' ? ' is-active' : ''}`}
          onClick={() => setActiveReport('expenses')}
        >
          <Receipt aria-hidden="true" /> {t('Expenses')}
        </button>
      </div>

      {/* Filters */}
      <div className="card rep-filters">
        <div className="rep-field">
          <label className="field-label">{t('Month')}:</label>
          <select 
            value={selectedMonth} 
            onChange={(e) => setSelectedMonth(parseInt(e.target.value))}
            className="select"
          >
            {months.map((month, index) => (
              <option key={index} value={index + 1}>{month}</option>
            ))}
          </select>
        </div>

        <div className="rep-field">
          <label className="field-label">{t('Year')}:</label>
          <select 
            value={selectedYear} 
            onChange={(e) => setSelectedYear(parseInt(e.target.value))}
            className="select"
          >
            {years.map(year => (
              <option key={year} value={year}>{year}</option>
            ))}
          </select>
        </div>

        {(activeReport === 'attendance' || activeReport === 'benefits') && (
          <div className="rep-field">
            <label className="field-label">{t('Employee')}:</label>
            <select 
              value={selectedEmployee} 
              onChange={(e) => setSelectedEmployee(e.target.value)}
              className="select"
            >
              <option value="all">{t('All Employees')}</option>
              {employees.map(emp => (
                <option key={emp.id} value={emp.id}>
                  {emp.first_name} {emp.last_name}
                </option>
              ))}
            </select>
          </div>
        )}

        <button onClick={loadReport} className="btn btn--secondary">
          <RefreshCw aria-hidden="true" /> {t('Refresh')}
        </button>
      </div>

      {/* Report Content */}
      {loading ? (
        <div className="empty">{t('Loading report...')}</div>
      ) : (
        <>
          {/* ATTENDANCE REPORT */}
          {activeReport === 'attendance' && (
            <AttendanceReportView 
              data={attendanceReport}
              formatDate={formatDate}
              formatTime={formatTime} 
              month={months[selectedMonth - 1]}
              year={selectedYear}
            />
          )}

          {truncated && (activeReport === 'payroll' || activeReport === 'taxes') && (
            <div className="rep-warning">
              <TriangleAlert aria-hidden="true" /> {t('Not all payrolls for this month could be loaded: the totals below are incomplete.')}
            </div>
          )}

          {/* PAYROLL SUMMARY */}
          {activeReport === 'payroll' && (
            <PayrollSummaryView 
              data={payrollSummary}
              formatCurrency={formatCurrency}
              formatTotals={formatTotals}
              formatDate={formatDate}
              month={months[selectedMonth - 1]}
              year={selectedYear}
            />
          )}

          {/* TAX REPORT */}
          {activeReport === 'taxes' && (
            <TaxReportView 
              data={taxReport}
              formatCurrency={formatCurrency}
              formatTotals={formatTotals}
              formatDate={formatDate}
              month={months[selectedMonth - 1]}
              year={selectedYear}
            />
          )}

          {/* BENEFITS REPORT */}
          {activeReport === 'benefits' && (
            <BenefitsReportView 
              data={benefitsReport}
              formatCurrency={formatCurrency}
              formatTotals={formatTotals}
              formatDate={formatDate}
            />
          )}

          {/* EXPENSES REPORT */}
          {activeReport === 'expenses' && (
            <ExpensesReportView 
              data={expensesReport}
              formatCurrency={formatCurrency}
              formatTotals={formatTotals}
              formatDate={formatDate}
              month={months[selectedMonth - 1]}
              year={selectedYear}
            />
          )}
        </>
      )}
    </div>
  );
};

// ==================== SUB-COMPONENTS ====================

// Attendance Report Component
const AttendanceReportView = ({ data, formatDate, formatTime, month, year }) => {
  const { t } = useLanguage();
  const stats = {
    total: data.length,
    present: data.filter(a => a.status === 'present').length,
    late: data.filter(a => a.status === 'late').length,
    absent: data.filter(a => a.status === 'absent').length,
    sick: data.filter(a => a.status === 'sick').length,
    vacation: data.filter(a => a.leave_type_id).length
  };

  return (
    <>
      <div className="kpi-grid">
        <div className="kpi">
          <h3 className="kpi__label">{t('Present')}</h3>
          <p className="kpi__value">{stats.present}</p>
          <span className="kpi__foot">{t('days present')}</span>
        </div>
        <div className="kpi">
          <h3 className="kpi__label">{t('Late')}</h3>
          <p className="kpi__value">{stats.late}</p>
          <span className="kpi__foot">{t('late arrivals')}</span>
        </div>
        <div className="kpi">
          <h3 className="kpi__label">{t('Absent')}</h3>
          <p className="kpi__value">{stats.absent}</p>
          <span className="kpi__foot">{t('absences')}</span>
        </div>
        <div className="kpi">
          <h3 className="kpi__label">{t('Sick/Leave')}</h3>
          <p className="kpi__value">{stats.sick + stats.vacation}</p>
          <span className="kpi__foot">{t('sick/leave days')}</span>
        </div>
      </div>

      <div className="card rep-section">
        <h2 className="rep-section__title">{t('Attendance Details')} - {month} {year}</h2>
        <div className="rep-table-wrap">
          <table className="data-table">
            <thead>
              <tr>
                <th>{t('Date')}</th>
                <th>{t('Employee')}</th>
                <th>{t('Clock In')}</th>
                <th>{t('Clock Out')}</th>
                <th className="num">{t('Hours')}</th>
                <th>{t('Status')}</th>
                <th className="num">{t('Late (min)')}</th>
                <th>{t('Notes')}</th>
              </tr>
            </thead>
            <tbody>
              {data.length === 0 ? (
                <tr>
                  <td colSpan="8" className="empty">
                    {t('No attendance records for this period')}
                  </td>
                </tr>
              ) : (
                data.map((record, index) => (
                  <tr key={index}>
                    <td>{formatDate(record.date)}</td>
                    <td><strong>{record.first_name} {record.last_name}</strong></td>
                    <td>{record.clock_in ? formatTime(record.clock_in) : '-'}</td>
                    <td>{record.clock_out ? formatTime(record.clock_out) : '-'}</td>
                    <td className="num">{parseFloat(record.hours_worked || 0).toFixed(1)} {t('h')}</td>
                    <td>
                      <StatusBadge status={record.status}>
                        {t(record.status || 'N/A')}
                      </StatusBadge>
                    </td>
                    <td className="num">{record.late_minutes || '-'}</td>
                    <td className="rep-notes">{record.notes || '-'}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
};

// Payroll Summary Component
const PayrollSummaryView = ({ data, formatCurrency, formatTotals, formatDate, month, year }) => {
  const { t } = useLanguage();
  if (!data) {
    return (
      <div className="card rep-section">
        <p className="empty">{t('No payroll data available')}</p>
      </div>
    );
  }

  return (
    <>
      <div className="kpi-grid">
        <div className="kpi">
          <h3 className="kpi__label">{t('Total Employees')}</h3>
          <p className="kpi__value">{data.total_employees}</p>
          <span className="kpi__foot">{t('paid this month')}</span>
        </div>
        <div className="kpi">
          <h3 className="kpi__label">{t('Gross Pay')}</h3>
          <p className="kpi__value">{formatTotals(data.total_gross)}</p>
          <span className="kpi__foot">{t('total earnings')}</span>
        </div>
        <div className="kpi">
          <h3 className="kpi__label">{t('Deductions')}</h3>
          <p className="kpi__value">{formatTotals(data.total_withheld)}</p>
          <span className="kpi__foot">{t('taxes + other')}</span>
        </div>
        <div className="kpi">
          <h3 className="kpi__label">{t('Net Pay')}</h3>
          <p className="kpi__value">{formatTotals(data.total_net)}</p>
          <span className="kpi__foot">{t('total payout')}</span>
        </div>
      </div>

      <div className="card rep-section">
        <h2 className="rep-section__title">{t('Payroll Summary')} - {month} {year}</h2>
        <div className="rep-breakdown">
          <div className="rep-breakdown__row">
            <span>{t('Total Gross Pay')}:</span>
            <strong>{formatTotals(data.total_gross)}</strong>
          </div>
          <div className="rep-breakdown__row is-minus">
            <span>- {t('Total Taxes')}:</span>
            <strong>{formatTotals(data.total_taxes)}</strong>
          </div>
          <div className="rep-breakdown__row is-minus">
            <span>- {t('Total Deductions')}:</span>
            <strong>{formatTotals(data.total_deductions)}</strong>
          </div>
          <div className="rep-breakdown__row is-total">
            <span>= {t('Total Net Pay')}:</span>
            <strong className="rep-net">{formatTotals(data.total_net)}</strong>
          </div>
        </div>

        <h3 className="rep-subtitle">{t('Employee Breakdown')}</h3>
        <div className="rep-table-wrap">
          <table className="data-table">
            <thead>
              <tr>
                <th>{t('Employee')}</th>
                <th>{t('Position')}</th>
                <th>{t('Payment Date')}</th>
                <th className="num">{t('Gross Pay')}</th>
                <th className="num">{t('Taxes')}</th>
                <th className="num">{t('Deductions')}</th>
                <th className="num">{t('Net Pay')}</th>
                <th>{t('Status')}</th>
              </tr>
            </thead>
            <tbody>
              {data.payrolls.map((payroll, index) => (
                <tr key={index}>
                  <td><strong>{payroll.first_name} {payroll.last_name}</strong></td>
                  <td>{payroll.position}</td>
                  <td>{formatDate(payroll.payment_date)}</td>
                  <td className="num">{formatCurrency(payroll.gross_pay, payroll.currency)}</td>
                  <td className="num rep-minus">{formatCurrency(payroll.tax_amount || payroll.total_taxes, payroll.currency)}</td>
                  <td className="num rep-minus">{formatCurrency(payroll.total_deductions, payroll.currency)}</td>
                  <td className="num rep-plus"><strong>{formatCurrency(payroll.net_pay, payroll.currency)}</strong></td>
                  <td>
                    <StatusBadge status={payroll.status}>
                      {t(payroll.status)}
                    </StatusBadge>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
};

// Tax Report Component
const TaxReportView = ({ data, formatCurrency, formatTotals, formatDate, month, year }) => {
  const { t } = useLanguage();
  // Group by tax type
  const taxSummary = data.reduce((acc, tax) => {
    const key = `${tax.tax_code}|${tax.currency}`;
    if (!acc[key]) {
      acc[key] = {
        name: tax.tax_name,
        code: tax.tax_code,
        currency: tax.currency,
        total: 0,
        count: 0,
        type: tax.tax_type
      };
    }
    acc[key].total += tax.tax_amount;
    acc[key].count += 1;
    return acc;
  }, {});

  const totalTaxes = totalsByCurrency(data, tax => tax.tax_amount, tax => tax.currency);

  return (
    <>
      <div className="kpi-grid">
        <div className="kpi">
          <h3 className="kpi__label">{t('Total Taxes')}</h3>
          <p className="kpi__value">{formatTotals(totalTaxes)}</p>
          <span className="kpi__foot">{t('collected this month')}</span>
        </div>
        <div className="kpi">
          <h3 className="kpi__label">{t('Tax Types')}</h3>
          <p className="kpi__value">{new Set(data.map(tx => tx.tax_code)).size}</p>
          <span className="kpi__foot">{t('different tax types')}</span>
        </div>
        <div className="kpi">
          <h3 className="kpi__label">{t('Employees')}</h3>
          <p className="kpi__value">{new Set(data.map(tx => tx.employee_id)).size}</p>
          <span className="kpi__foot">{t('taxpayers')}</span>
        </div>
      </div>

      <div className="card rep-section">
        <h2 className="rep-section__title">{t('Tax Summary by Type')} - {month} {year}</h2>
        <div className="rep-table-wrap">
          <table className="data-table">
            <thead>
              <tr>
                <th>{t('Tax Code')}</th>
                <th>{t('Tax Name')}</th>
                <th>{t('Type')}</th>
                <th className="num">{t('Count')}</th>
                <th className="num">{t('Total Amount')}</th>
              </tr>
            </thead>
            <tbody>
              {Object.values(taxSummary).map((tax, index) => (
                <tr key={index}>
                  <td><strong>{tax.code}</strong></td>
                  <td>{tax.name}</td>
                  <td>
                    <StatusBadge variant="neutral">
                      {t(tax.type)}
                    </StatusBadge>
                  </td>
                  <td className="num">{tax.count}</td>
                  <td className="num"><strong>{formatCurrency(tax.total, tax.currency)}</strong></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="card rep-section">
        <h2 className="rep-section__title">{t('Tax Details by Employee')}</h2>
        <div className="rep-table-wrap">
          <table className="data-table">
            <thead>
              <tr>
                <th>{t('Employee')}</th>
                <th>{t('Payment Date')}</th>
                <th>{t('Tax Code')}</th>
                <th>{t('Tax Name')}</th>
                <th className="num">{t('Rate')} (%)</th>
                <th className="num">{t('Amount')}</th>
              </tr>
            </thead>
            <tbody>
              {data.length === 0 ? (
                <tr>
                  <td colSpan="6" className="empty">
                    {t('No tax data for this period')}
                  </td>
                </tr>
              ) : (
                data.map((tax, index) => (
                  <tr key={index}>
                    <td><strong>{tax.employee_name}</strong></td>
                    <td>{formatDate(tax.payment_date)}</td>
                    <td>{tax.tax_code}</td>
                    <td>{tax.tax_name}</td>
                    <td className="num">{tax.tax_rate ? tax.tax_rate.toFixed(2) : '-'}</td>
                    <td className="num"><strong>{formatCurrency(tax.tax_amount, tax.currency)}</strong></td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
};

// Benefits Report Component
const BenefitsReportView = ({ data, formatCurrency, formatTotals, formatDate }) => {
  const { t } = useLanguage();
  const totalBenefits = totalsByCurrency(data, b => b.amount, b => b.currency);

  return (
    <>
      <div className="kpi-grid">
        <div className="kpi">
          <h3 className="kpi__label">{t('Active Benefits')}</h3>
          <p className="kpi__value">{data.length}</p>
          <span className="kpi__foot">{t('total assignments')}</span>
        </div>
        <div className="kpi">
          <h3 className="kpi__label">{t('Total Value')}</h3>
          <p className="kpi__value">{formatTotals(totalBenefits)}</p>
          <span className="kpi__foot">{t('per month')}</span>
        </div>
        <div className="kpi">
          <h3 className="kpi__label">{t('Employees')}</h3>
          <p className="kpi__value">{new Set(data.map(b => b.employee_id)).size}</p>
          <span className="kpi__foot">{t('receiving benefits')}</span>
        </div>
      </div>

      <div className="card rep-section">
        <h2 className="rep-section__title">{t('Active Benefits by Employee')}</h2>
        <div className="rep-table-wrap">
          <table className="data-table">
            <thead>
              <tr>
                <th>{t('Employee')}</th>
                <th>{t('Benefit')}</th>
                <th>{t('Type')}</th>
                <th className="num">{t('Amount')}</th>
                <th>{t('Start Date')}</th>
                <th>{t('End Date')}</th>
                <th>{t('Status')}</th>
              </tr>
            </thead>
            <tbody>
              {data.length === 0 ? (
                <tr>
                  <td colSpan="7" className="empty">
                    {t('No active benefits')}
                  </td>
                </tr>
              ) : (
                data.map((benefit, index) => (
                  <tr key={index}>
                    <td><strong>{benefit.first_name} {benefit.last_name}</strong></td>
                    <td>{benefit.benefit_name}</td>
                    <td>{t(benefit.calculation_type)}</td>
                    <td className="num"><strong>{formatCurrency(benefit.amount, benefit.currency)}</strong></td>
                    <td>{formatDate(benefit.start_date)}</td>
                    <td>{formatDate(benefit.end_date) || t('Ongoing')}</td>
                    <td>
                      <StatusBadge status={benefit.status}>
                        {t(benefit.status)}
                      </StatusBadge>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
};

// Expenses Report Component
const ExpensesReportView = ({ data, formatCurrency, formatTotals, formatDate, month, year }) => {
  const { t } = useLanguage();
  const totalExpenses = totalsByCurrency(data, e => e.amount, e => e.currency);
  const approvedExpenses = data.filter(e => e.status === 'approved');
  const totalApproved = totalsByCurrency(approvedExpenses, e => e.amount, e => e.currency);

  return (
    <>
      <div className="kpi-grid">
        <div className="kpi">
          <h3 className="kpi__label">{t('Total Expenses')}</h3>
          <p className="kpi__value">{formatTotals(totalExpenses)}</p>
          <span className="kpi__foot">{t('this month')}</span>
        </div>
        <div className="kpi">
          <h3 className="kpi__label">{t('Approved')}</h3>
          <p className="kpi__value">{formatTotals(totalApproved)}</p>
          <span className="kpi__foot">{t('{count} expenses', { count: approvedExpenses.length })}</span>
        </div>
        <div className="kpi">
          <h3 className="kpi__label">{t('Pending')}</h3>
          <p className="kpi__value">{data.filter(e => e.status === 'pending').length}</p>
          <span className="kpi__foot">{t('awaiting approval')}</span>
        </div>
      </div>

      <div className="card rep-section">
        <h2 className="rep-section__title">{t('Expenses')} - {month} {year}</h2>
        <div className="rep-table-wrap">
          <table className="data-table">
            <thead>
              <tr>
                <th>{t('Date')}</th>
                <th>{t('Category')}</th>
                <th>{t('Description')}</th>
                <th className="num">{t('Amount')}</th>
                <th>{t('Vendor')}</th>
                <th>{t('Status')}</th>
                <th>{t('Approved By')}</th>
              </tr>
            </thead>
            <tbody>
              {data.length === 0 ? (
                <tr>
                  <td colSpan="7" className="empty">
                    {t('No expenses for this period')}
                  </td>
                </tr>
              ) : (
                data.map((expense, index) => (
                  <tr key={index}>
                    <td>{formatDate(expense.date)}</td>
                    <td>{expense.category}</td>
                    <td className="rep-notes">{expense.description}</td>
                    <td className="num"><strong>{formatCurrency(expense.amount, expense.currency)}</strong></td>
                    <td>{expense.vendor_name || '-'}</td>
                    <td>
                      <StatusBadge status={expense.status}>
                        {t(expense.status)}
                      </StatusBadge>
                    </td>
                    <td>{expense.approved_by_name || '-'}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
};


export default Reports;
